import { Link } from 'react-router-dom'
import {
  ShieldCheck,
  Clock,
  AlertTriangle,
  ChevronRight,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { AdminAttestation } from '@/hooks/useAdminAttestations'

interface FrameworkInfo {
  name: string
  product: string
  color: string
}

interface AttestationReviewCardProps {
  attestation: AdminAttestation
  frameworkInfo?: FrameworkInfo
  isLoading?: boolean
  onApprove?: () => void
  onReject?: () => void
}

export default function AttestationReviewCard({
  attestation,
  frameworkInfo,
  isLoading = false,
  onApprove,
  onReject,
}: AttestationReviewCardProps) {
  const isPriority = attestation.days_pending > 3

  return (
    <div
      className={`bg-white rounded-xl border transition-all ${
        isPriority
          ? 'border-amber-300 shadow-amber-100 shadow-sm'
          : 'border-gray-200 hover:border-gray-300'
      } ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          {/* Left Side - Info */}
          <div className="flex items-start gap-4 flex-1 min-w-0">
            {/* Framework Icon */}
            <div className={`p-2.5 rounded-lg flex-shrink-0 ${frameworkInfo?.color || 'bg-gray-100'}`}>
              <ShieldCheck className="w-5 h-5" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Framework & Tenant */}
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900">
                  {frameworkInfo?.name || attestation.framework.toUpperCase()}
                </h3>
                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                  {frameworkInfo?.product}
                </span>
                {isPriority && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                    <AlertTriangle className="w-3 h-3" />
                    Priority
                  </span>
                )}
              </div>

              {/* Tenant Name */}
              <p className="text-gray-900 font-medium mt-1">
                {attestation.tenant_name || 'Unknown Tenant'}
              </p>

              {/* Attestor Info */}
              <p className="text-sm text-gray-500 mt-0.5">
                {attestation.attested_by_name} ({attestation.attested_by_title})
              </p>

              {/* Timing Info */}
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  Submitted {formatDate(attestation.submitted_at)}
                </span>
                <span className={`font-medium ${isPriority ? 'text-amber-600' : 'text-gray-600'}`}>
                  {attestation.days_pending} days pending
                </span>
              </div>

              {/* Previous review notes warning */}
              {attestation.review_notes && (
                <div className="mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded inline-block">
                  Changes requested: {attestation.review_notes.substring(0, 50)}...
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Quick Actions */}
            {onApprove && onReject && (
              <div className="hidden sm:flex items-center gap-2 mr-2">
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    onApprove()
                  }}
                  disabled={isLoading}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 disabled:opacity-50"
                  title="Quick Approve"
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  Approve
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    onReject()
                  }}
                  disabled={isLoading}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50"
                  title="Quick Reject"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  Reject
                </button>
              </div>
            )}

            {/* Review Link */}
            <Link
              to={`/employee/attestations/${attestation.id}`}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-personic-600 bg-personic-50 rounded-lg hover:bg-personic-100 transition-colors"
            >
              Review
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return 'today'
  } else if (diffDays === 1) {
    return 'yesterday'
  } else if (diffDays < 7) {
    return `${diffDays} days ago`
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
  }
}
